



















































import Vue from 'vue';
import { Component, Inject, Ref, Watch } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import { Route } from 'vue-router';
import { FilterBar } from '@/lazy-load-components';
import ContentHeader from '@/components/content-header/ContentHeader.vue';

import LoadSurvey from '@/components/survey/LoadSurvey.vue';
import TitleTimelineGraphql from '@/components/timeline/TitleTimelineGraphql.vue';

import { WebLocale } from '@/enums/web-locale';
import { CollectionType } from '@/enums/collection-type';

import {
	PageNavigationTransaction,
	sentryPerformanceTracker,
} from '@/helpers/tracking/performance/SentryPerformanceTracker';
import TitleHelper, { TitleEventPayloadGraphql } from '@/helpers/title-helper';
import ToastHelper from '@/helpers/toast/toast-helper';
import {
	GetNewTitleBucketsDocument,
	GetNewTitleBucketsQueryVariables,
} from '@/pages/graphql/queries/GetNewTitleBuckets.query';
import { getCollectionTypeByNewPageType, toGraphqlFilters } from '@/helpers/graphql-model-helper';
import { captureMessageForSentry, Sentry } from '@/helpers/sentry-helper';

import { FilterCollection } from '@/stores/filter.store';
import { UserSettings } from '@/stores/user.store';

import { FormattedTimelineData, NewPageBucketsData } from '@/interfaces/new-title-graphql';
import { Content } from '@/interfaces/components/content';
import { NewPageType, ObjectType, TitleFilter } from '@/@types/graphql-types';
import FilterHelper from '@/helpers/filter-helper';
import NewProBanner from '@/NewProBanner.vue';
import { isProWebCountry } from '@/helpers/pro-web-helper';

const NewPageTypeSwitcher = () =>
	import(/* webpackChunkName: "pricedrop-switcher" */ '@/components/nav/switcher/NewPageTypeSwitcher.vue');
const FallbackTitles = () =>
	import(/* webpackChunkName: "fallback-titles" */ '@/components/timeline/FallbackTitles.vue');

const Filter = namespace('filter');
const Language = namespace('language');
const Routing = namespace('routing');
const Ui = namespace('ui');
const User = namespace('user');
const Experiment = namespace('experiment');
import { BucketData } from '@/interfaces/new-title-graphql';

type GroupedItems = {
	[key: string]: BucketData[];
};

function groupBy(items: BucketData[], key: string): GroupedItems | null {
	return items?.reduce((acc, item) => {
		const groupValue: string | undefined = key.includes('bucketType') ? item.key.bucketType : item.key.date;
		if (!groupValue) return acc;
		if (!acc[groupValue]) {
			acc[groupValue] = [];
		}
		acc[groupValue].push(item);
		return acc;
	}, {} as GroupedItems);
}

@Component<New>({
	components: {
		NewProBanner,
		TitleTimelineGraphql,
		NewPageTypeSwitcher,
		ContentHeader,
		FilterBar,
		LoadSurvey,
		FallbackTitles,
	},
	name: 'New',
	apollo: {
		newPageData: {
			query: GetNewTitleBucketsDocument,
			variables(): GetNewTitleBucketsQueryVariables {
				return {
					newAfterCursor: '',
					newTitlesFilter: {
						...this.graphqlFilters,
						objectTypes: this.filterObjectTypes,
					},
					country: this.country,
					priceDrops: this.isPriceDropView,
					bucketSize: 0,
					first: this.isLeavingOrComingSoon && !this.isPremium ? 1 : 5,
					pageType: this.newPageType,
				};
			},
			update(data) {
				this.isFirstPageLoaded[this.newPageType] = true;
				return data.newTitleBuckets;
			},
			error() {
				process.client ? this.showErrorToast() : null;
			},
		},
	},
	beforeRouteEnter: (to: Route, from: Route, next: Function) => {
		next((vm: New) => {
			// We want users in countries where Pro is available to return to the last active New subpage,
			// i.e. if a user was looking at Leaving Soon, then went to Popular and then back to New,
			// we want them to see Leaving soon, although the link in the navbar leads to the original New page

			if (
				!isProWebCountry(vm.webLocale) &&
				!vm.isPremium &&
				[NewPageType.LeavingSoon, NewPageType.Upcoming].includes(to.meta?.newPageType)
			) {
				const newPageRoute = vm.routeParams(CollectionType.NEW);
				vm.setLastActiveNewPageType(NewPageType.New);
				vm.$router.push(newPageRoute);
				return;
			}

			if (
				!from.meta?.newPageType &&
				vm.lastActiveNewPageType !== to?.meta?.newPageType &&
				isProWebCountry(vm.webLocale)
			) {
				const lastActiveNewPageRoute = vm.routeParams(
					vm.getCollectionTypeByNewPageType(vm.lastActiveNewPageType) as CollectionType
				);
				vm.$router.push(lastActiveNewPageRoute);
			} else {
				vm.setLastActiveNewPageType(to?.meta?.newPageType);
			}
		});
	},
})
export default class New extends Vue {
	getCollectionTypeByNewPageType = getCollectionTypeByNewPageType;
	newPageData: NewPageBucketsData = {} as NewPageBucketsData;
	private showFooter = false;
	isFirstPageLoaded: Record<NewPageType, boolean> = {
		[NewPageType.New]: false,
		[NewPageType.LeavingSoon]: false,
		[NewPageType.Upcoming]: false,
		[NewPageType.PriceDrop]: false,
	};
	CollectionType = CollectionType;

	@Experiment.Getter activeVariantsWithControlGroup: Record<string, string>;

	@Filter.Getter(CollectionType.NEW) filtersNew: FilterCollection;

	@Language.State webLocale: WebLocale;
	@Language.Getter language: string;
	@Language.Getter country: string;

	@Routing.Getter activeCollectionType: CollectionType;
	@Routing.State activeRoute: any;

	@Ui.State showFilterBar: boolean;

	@User.State settings: UserSettings;
	@User.Getter isHideSeenDislikedTitlesEnabled: boolean;
	@User.Getter lastActiveNewPageType: NewPageType;
	@User.Getter isPremium: boolean;
	@User.Action setLastActiveNewPageType: (collectionType: NewPageType) => void;

	@Inject('routeParams') routeParams: (collectionType: CollectionType) => any;

	@Ref('content') readonly content!: Content;
	@Ref('titleTimeline') readonly titleTimeline!: TitleTimelineGraphql;

	get isLeavingOrComingSoon() {
		return this.newPageType === NewPageType.LeavingSoon || this.newPageType === NewPageType.Upcoming;
	}

	get showNewProBanner() {
		return !this.isPremium && this.isLeavingOrComingSoon && !this.isLoading;
	}

	get isPriceDropView() {
		return this.newPageType === NewPageType.PriceDrop;
	}

	get newPageType() {
		return this.$route?.meta?.newPageType as NewPageType;
	}

	get formattedTimelineData(): FormattedTimelineData[] {
		const groupByProperty = this.isLeavingOrComingSoon ? 'key.bucketType' : 'key.date';
		const groupedBuckets = groupBy(this.newPageData.edges, groupByProperty);
		if (!groupedBuckets) return [];
		return Object.keys(groupedBuckets ?? '').reduce((newPageDays, key, currentIndex) => {
			/*
       Due to a race condition when changing between Leaving soon/Coming soon and New/Pricedrops
       groupBy() produces an object with a property "undefined" (as a string, not a type), that then used as a key for buckets.
       It causes some visual artifacts and wrong GraphQL requests when switch between pages. This hack prevents it.
      */
			if (key === 'undefined') {
				return [];
			}

			/*
			 * Show only first bucket to non pro users
			 * */
			if (!this.isPremium && currentIndex > 0 && this.isLeavingOrComingSoon) {
				return newPageDays;
			}

			return [
				...newPageDays,
				{
					key,
					isGroupedByDate: groupByProperty !== 'key.bucketType',
					providers:
						this.isLeavingOrComingSoon && !this.isPremium
							? groupedBuckets[key].slice(0, 1)
							: groupedBuckets[key],
				},
			];
		}, [] as FormattedTimelineData[]);
	}

	get showSpinner() {
		return !this.isFirstPageLoaded[this.newPageType] && this.isLoading;
	}

	// We need this mapper due to filter naming on the New Page
	// We have a TV Show filter on New Page that needs to fetch only TV Show Seasons items
	get filterObjectTypes() {
		return this.graphqlFilters?.objectTypes?.map(objectType =>
			objectType === ObjectType.Show ? ObjectType.ShowSeason : objectType
		);
	}

	get showFallbackTitles() {
		const activeFilters = FilterHelper.getActiveFilters(Object.keys(this.filtersNew) as any, this.filtersNew);

		return (
			activeFilters.content_types &&
			activeFilters.providers &&
			Object.keys(activeFilters).length === 2 &&
			(this.newPageData.edges ?? []).length === 0
		);
	}

	@Watch('isTitlesLoading')
	onIsTitlesLoading(isTitlesLoading: boolean) {
		if (isTitlesLoading) return;
		sentryPerformanceTracker.finishTransaction(PageNavigationTransaction.New);
	}

	@Watch('filtersNew')
	changedFilters() {
		this.isFirstPageLoaded[this.newPageType] = false;

		this.titleTimeline?.resetInfiniteScroll();

		// scroll to top if the filters have changed
		this.scrollToTop();
	}

	get isLoading() {
		return this.$apollo.queries.newPageData.loading;
	}

	get isFirstPage() {
		return !this.newPageData?.pageInfo?.hasPreviousPage;
	}

	get isAtEndOfList() {
		return !this.newPageData?.pageInfo?.hasNextPage || this.showNewProBanner;
	}

	get graphqlFilters(): TitleFilter {
		const selectedProviders =
			this.filtersNew.providers?.length === 0 ? this.settings.providers : this.filtersNew.providers;
		return toGraphqlFilters(this.filtersNew, selectedProviders, this.isHideSeenDislikedTitlesEnabled);
	}

	mounted() {}

	scrollToTop() {
		this.content?.scrollToTop();
	}

	trackClick(payload: TitleEventPayloadGraphql) {
		TitleHelper.trackTitleEventGraphql('title_clicked', payload, 'new-timeline');
	}

	showErrorToast() {
		ToastHelper.error({ message: 'Something went wrong, please try again' });
	}

	onLoadMore({ event }: { event: any }) {
		event &&
			this.$apollo.queries.newPageData
				.fetchMore({
					variables: {
						newAfterCursor: this.newPageData.pageInfo.endCursor,
					},
					updateQuery(previousResult, { fetchMoreResult }) {
						return {
							newTitleBuckets: {
								__typename: previousResult.newTitleBuckets.__typename,
								edges: [
									...previousResult.newTitleBuckets.edges,
									...fetchMoreResult.newTitleBuckets.edges,
								],
								pageInfo: fetchMoreResult.newTitleBuckets.pageInfo,
							},
						};
					},
				})
				.catch(e => {
					captureMessageForSentry(
						'[GraphQL New Page Load More Buckets Issue]:',
						{ error: e, where: 'Component: New, method: onLoadMore' },
						Sentry.Severity.Error
					);
				})
				.finally(() => event.target && event.target.complete(this.newPageData.edges?.length));
	}
}
